import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Zap, Shield, BarChart3, ArrowRight, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Bot className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold text-slate-900">RAGBot</span>
            </div>
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <Link 
                  to="/dashboard" 
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-slate-600 hover:text-slate-900 font-medium">
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium"
                  >
                    Get Started Free
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="h-4 w-4 mr-2" />
            AI-Powered Customer Support
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Train AI Chatbots on
            <span className="text-blue-600"> Your Documents</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Upload your company's knowledge base and deploy intelligent chatbots that answer questions 
            accurately using your own data. No hallucinations, just facts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register" 
              className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition font-semibold text-lg flex items-center justify-center"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <button className="bg-white text-slate-700 px-8 py-4 rounded-lg hover:bg-slate-50 transition font-semibold text-lg border border-slate-200">
              Watch Demo
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Everything You Need</h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Powerful features to build, deploy, and manage your AI chatbot
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 rounded-xl border border-slate-200 hover:shadow-lg transition">
              <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Bot className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Smart AI Chatbots</h3>
              <p className="text-slate-600">
                Configure personality, tone, and behavior. Your chatbot sounds like your brand.
              </p>
            </div>
            
            <div className="p-6 rounded-xl border border-slate-200 hover:shadow-lg transition">
              <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Data Security</h3>
              <p className="text-slate-600">
                Multi-tenant architecture ensures your data never mixes with other businesses.
              </p>
            </div>
            
            <div className="p-6 rounded-xl border border-slate-200 hover:shadow-lg transition">
              <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Analytics Dashboard</h3>
              <p className="text-slate-600">
                Track conversations, token usage, and customer insights in real-time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-slate-600 text-lg">Get started in minutes</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Upload Documents</h3>
              <p className="text-slate-600">
                Upload PDFs, Word docs, or any text files. We'll process and index them automatically.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Configure Chatbot</h3>
              <p className="text-slate-600">
                Customize your chatbot's personality, welcome message, and system instructions.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Embed & Deploy</h3>
              <p className="text-slate-600">
                Copy a simple script and paste it into your website. Your chatbot is live!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Simple Pricing</h2>
            <p className="text-slate-600 text-lg">Start free, scale as you grow</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="p-8 rounded-xl border border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Free</h3>
              <div className="text-3xl font-bold text-slate-900 mb-4">$0<span className="text-lg font-normal text-slate-600">/mo</span></div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  1 Chatbot
                </li>
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  50 Documents
                </li>
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  10,000 Tokens/mo
                </li>
              </ul>
              <Link to="/register" className="block text-center bg-slate-100 text-slate-700 px-6 py-3 rounded-lg hover:bg-slate-200 transition font-medium">
                Get Started
              </Link>
            </div>
            
            <div className="p-8 rounded-xl border-2 border-blue-600 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                Popular
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Pro</h3>
              <div className="text-3xl font-bold text-slate-900 mb-4">$49<span className="text-lg font-normal text-slate-600">/mo</span></div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  5 Chatbots
                </li>
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  500 Documents
                </li>
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  100,000 Tokens/mo
                </li>
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Priority Support
                </li>
              </ul>
              <Link to="/register" className="block text-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium">
                Start Free Trial
              </Link>
            </div>
            
            <div className="p-8 rounded-xl border border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Business</h3>
              <div className="text-3xl font-bold text-slate-900 mb-4">$199<span className="text-lg font-normal text-slate-600">/mo</span></div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Unlimited Chatbots
                </li>
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Unlimited Documents
                </li>
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  1,000,000 Tokens/mo
                </li>
                <li className="flex items-center text-slate-600">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Dedicated Support
                </li>
              </ul>
              <Link to="/register" className="block text-center bg-slate-100 text-slate-700 px-6 py-3 rounded-lg hover:bg-slate-200 transition font-medium">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Transform Your Customer Support?</h2>
          <p className="text-blue-100 text-lg mb-8">
            Join thousands of businesses using RAGBot to deliver instant, accurate answers.
          </p>
          <Link 
            to="/register" 
            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-blue-50 transition font-semibold text-lg"
          >
            Start Your Free Trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Bot className="h-6 w-6 text-blue-400" />
            <span className="text-lg font-bold text-white">RAGBot</span>
          </div>
          <p className="text-sm">
            © 2024 RAGBot. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
